import React, { memo, useCallback } from 'react';
import { VirtualizedList, Dimensions } from 'react-native';
import { Box, VStack, Text, Select, CheckIcon } from 'native-base';
import LazyImage from 'src/components/LazyImage';
import { IImageItem, IRejectionMapping } from 'src/models/UploadImages';

interface ImageItemProps {
  item: IImageItem;
  currentLabelKey: string;
  rejectionMapping: IRejectionMapping;
  onLabelChange: (imagePath: string, labelKey: string) => void;
  isExistingDataset?: boolean;
}

const ImageItem: React.FC<ImageItemProps> = memo(
  ({ item, currentLabelKey, rejectionMapping, onLabelChange, isExistingDataset }) => {
    const imagePathDisplay = item.imagePath?.split('/').pop() ?? 'Unknown';
    const options = { ...rejectionMapping };
    if (currentLabelKey && !options[currentLabelKey]) {
      options['customLabel'] = 'Custom Label';
    }

    return (
      <Box
        flex={1}
        m={2}
        borderWidth="1"
        borderColor="coolGray.200"
        borderRadius="md"
        bg="white"
        overflow="hidden"
      >
        <VStack space={2}>
          <Box width="100%" height={200} justifyContent="center" alignItems="center">
            <LazyImage
              source={{ uri: isExistingDataset ? item.imageUrl : item.imageUrl }}
              style={{
                width: '100%',
                height: '100%',
                resizeMode: 'contain',
              }}
            />
          </Box>
          <Text fontSize="sm" bold numberOfLines={1}>
            {imagePathDisplay}
          </Text>
          <Select
            selectedValue={currentLabelKey}
            minWidth="100%"
            placeholder="Select Label"
            _selectedItem={{
              bg: 'cyan.600',
              endIcon: <CheckIcon size="5" />,
            }}
            onValueChange={(value) => onLabelChange(item.imagePath, value)}
          >
            {Object.entries(options).map(([key, value]) => (
              <Select.Item key={key} label={value} value={key} />
            ))}
          </Select>
        </VStack>
      </Box>
    );
  }
);

interface ImageTableProps {
  images: IImageItem[];
  changedLabels: { [imagePath: string]: string };
  rejectionMapping: IRejectionMapping;
  onLabelChange: (imagePath: string, labelKey: string) => void;
  isExistingDataset?: boolean;
}

const ImageTable: React.FC<ImageTableProps> = memo(
  ({ images, changedLabels, rejectionMapping, onLabelChange, isExistingDataset }) => {
    const screenWidth = Dimensions.get('window').width;
    const numColumns = 2;
    const itemWidth = (screenWidth - 60) / numColumns; // 60 for padding and margins
    const itemHeight = 300; // Fixed height for better virtualization

    const getItem = useCallback(
      (data: IImageItem[], index: number) => {
        const rowIndex = Math.floor(index / numColumns);
        const colIndex = index % numColumns;
        const itemIndex = rowIndex * numColumns + colIndex;
        return data[itemIndex];
      },
      [numColumns]
    );

    const getItemCount = useCallback(
      (data: IImageItem[]) => Math.ceil(data.length / numColumns),
      [numColumns]
    );

    const renderRow = useCallback(
      ({ index }: { index: number }) => {
        const startIndex = index * numColumns;
        const rowItems = images.slice(startIndex, startIndex + numColumns);

        return (
          <Box flexDirection="row" width="100%" justifyContent="space-between" mb={2}>
            {rowItems.map((item, colIndex) => {
              if (!item) return <Box key={colIndex} width={itemWidth} />;

              const changedLabelKey = changedLabels[item.imagePath];
              const originalLabelKey = rejectionMapping[item.label]
                ? item.label
                : getKeyByValue(rejectionMapping, item.label);
              const currentLabelKey = changedLabelKey || originalLabelKey || '';

              return (
                <Box key={item.imagePath || `${index}-${colIndex}`} width={itemWidth}>
                  <ImageItem
                    item={item}
                    currentLabelKey={currentLabelKey}
                    rejectionMapping={rejectionMapping}
                    onLabelChange={onLabelChange}
                    isExistingDataset={isExistingDataset}
                  />
                </Box>
              );
            })}
          </Box>
        );
      },
      [images, changedLabels, rejectionMapping, onLabelChange, isExistingDataset, numColumns, itemWidth]
    );

    const keyExtractor = useCallback(
      (item: IImageItem, index: number) => `row-${index}`,
      []
    );

    if (images.length === 0) {
      return <Text>No images to display.</Text>;
    }

    return (
      <VirtualizedList
        data={images}
        initialNumToRender={4}
        maxToRenderPerBatch={6}
        windowSize={10}
        removeClippedSubviews={true}
        getItemCount={getItemCount}
        getItem={getItem}
        renderItem={renderRow}
        keyExtractor={keyExtractor}
        getItemLayout={(data, index) => ({
          length: itemHeight,
          offset: itemHeight * index,
          index,
        })}
        extraData={changedLabels}
        contentContainerStyle={{ paddingBottom: 20 }}
      />
    );
  }
);

export default ImageTable;

export const getKeyByValue = (mapping: { [key: string]: string }, value: string) => {
  return Object.keys(mapping).find((key) => mapping[key] === value);
};